import {Serializer} from "../interfaces/serializer";
import {BehaviorSubject} from "rxjs";
import {SavedReportFolder} from "../interfaces/saved-report-folder";
import {SavedReportComponentObject, SavedReportComponentObjects} from "./saved-report-component-object";

export type FolderComponentObjects = Array<FolderComponentObject>;

export class FolderComponentObject {
  public payload: Serializer<SavedReportFolder>;

  public readonly name$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly reportCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly folderCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalReportCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalFolderCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public readonly fullPath$: BehaviorSubject<FolderComponentObjects> = new BehaviorSubject<FolderComponentObjects>([]);
  public readonly path$: BehaviorSubject<FolderComponentObjects> = new BehaviorSubject<FolderComponentObjects>([]);

  public readonly folder$: BehaviorSubject<FolderComponentObject> = new BehaviorSubject<FolderComponentObject>(null);
  public readonly folders$: BehaviorSubject<FolderComponentObjects> = new BehaviorSubject<FolderComponentObjects>([]);
  public readonly reports$: BehaviorSubject<SavedReportComponentObjects> = new BehaviorSubject<SavedReportComponentObjects>([]);

  constructor(payload: Serializer<SavedReportFolder>) {
    this.update(payload);
  }

  public loading(): void {
    this.loading$.next(true);
  }

  public update(payload: Serializer<SavedReportFolder>): void {
    this.payload = payload;

    this.name$.next(payload.attributes.name);
    this.loading$.next(false);
  }

  public init(folders: FolderComponentObjects, reports: SavedReportComponentObjects = []): void {
    const foldersFound: FolderComponentObjects = folders.filter((folder: FolderComponentObject): boolean => folder.payload.attributes.parent_folder_id === this.payload.id);
    const reportsFound: SavedReportComponentObjects = reports.filter((report: SavedReportComponentObject): boolean => report.payload.attributes.saved_reports_folder_id === this.payload.id);

    this.folders$.next(foldersFound);
    this.reports$.next(reportsFound);
    this.fullPath$.next(this._getPath(this, folders));
    this.path$.next(this.fullPath$.value.slice(0, this.fullPath$.value.length - 1));
    this.folderCount$.next(foldersFound.length);
    this.reportCount$.next(reportsFound.length);

    this.folder$.next(
      folders.find((folder: FolderComponentObject): boolean => folder.payload.id === this.payload.attributes.parent_folder_id) ||
      folders.find((folder: FolderComponentObject): boolean => folder.payload.id === null)
    );

    const totals: { reports: number, folders: number } = this._getTotals(this, folders, reports);

    this.totalReportCount$.next(totals.reports);
    this.totalFolderCount$.next(totals.folders);
  }

  public getFullPathToStr(): string {
    return this.fullPath$.getValue().map((folder: FolderComponentObject) => folder.payload.attributes.name).join(' > ');
  }

  private _getPath(folder: FolderComponentObject, folders: FolderComponentObjects): FolderComponentObjects {
    const foldersFound: FolderComponentObject = folders.find((f: FolderComponentObject) => f.payload.id === folder.payload.attributes.parent_folder_id);

    if (folder.payload.id === null || !foldersFound) {
      return [folder];
    }

    return [
      ...this._getPath(foldersFound, folders),
      folder
    ];
  }

  private _getTotals(currentFolder: FolderComponentObject, folders: FolderComponentObjects, reports: SavedReportComponentObjects, output: { reports: number, folders: number } = { reports: 0, folders: 0 }): { reports: number, folders: number } {
    const foldersFound: FolderComponentObjects = folders.filter((folder: FolderComponentObject): boolean => folder.payload.attributes.parent_folder_id === currentFolder.payload.id);
    const reportsFound: SavedReportComponentObjects = reports.filter((report: SavedReportComponentObject): boolean => report.payload.attributes.saved_reports_folder_id === currentFolder.payload.id);

    output.reports += reportsFound.length;
    output.folders += foldersFound.length;

    for (const folderFound of foldersFound) {
      this._getTotals(folderFound, folders, reports, output);
    }

    return output;
  }

}
